import { useTranslation } from 'react-i18next';

import type { PortalSubscriptionPlan } from '@/modules/portal/types/portalPlans';
import type { PortalPricingPlanPrice } from '@/modules/portal/types/portalPricingCatalog';
import {
  extractPortalPlanFeatureLines,
  isFreeTrialPlan,
  isPortalUpgradeCtaEnabled,
  resolvePortalPlanCtaLabelKey,
  resolvePortalPlanDescription,
  resolvePortalPlanDisplayName,
  translatePortalPlanDisabledReason,
  translatePortalPlanFeatureLine,
} from '@/modules/portal/utils/portalPlanHelpers';
import { Button } from '@/shared/components/ui/button';
import { Icons } from '@/shared/components/ui/icons';
import { cn } from '@/shared/lib/cn';

type SubscriptionPlanCardProps = {
  plan: PortalSubscriptionPlan;
  locale: string;
  className?: string;
  onUpgradeClick?: (plan: PortalSubscriptionPlan) => void;
  displayPrice?: PortalPricingPlanPrice | null;
};

function planIcon(planCode: string) {
  if (planCode.includes('professional')) return Icons.CheckCircle;
  if (planCode.includes('advanced')) return Icons.Layers;
  return Icons.Package;
}

export function SubscriptionPlanCard({
  plan,
  locale,
  className,
  onUpgradeClick,
  displayPrice = null,
}: SubscriptionPlanCardProps) {
  const { t } = useTranslation();

  const displayName = resolvePortalPlanDisplayName(plan, locale);
  const language = locale.startsWith('ar') ? 'ar' : 'en';
  const priceLabel = displayPrice
    ? new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: displayPrice.currency,
        maximumFractionDigits: 4,
      }).format(displayPrice.price)
    : language === 'ar'
      ? 'غير متاح'
      : 'Unavailable';

  const originalPriceLabel =
    displayPrice?.originalPrice !== undefined && displayPrice.originalPrice > displayPrice.price
      ? new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: displayPrice.currency,
          maximumFractionDigits: 4,
        }).format(displayPrice.originalPrice)
      : null;

  const description = resolvePortalPlanDescription(plan, locale);
  const featureLines = extractPortalPlanFeatureLines(plan.features).map((feature) =>
    translatePortalPlanFeatureLine(feature, locale),
  );

  const disabledReason = translatePortalPlanDisabledReason(plan.disabled_reason, t);
  const ctaLabel = t(resolvePortalPlanCtaLabelKey(plan.cta_type));
  const isCurrent = plan.is_current || plan.cta_type === 'current';
  const isUpgradeEnabled =
    (isPortalUpgradeCtaEnabled(plan) || plan.cta_type === 'upgrade') && displayPrice !== null;
  const Icon = planIcon(plan.plan_code || plan.slug);

  return (
    <article
      className={cn(
        'portal-glass-panel flex h-full min-w-[340px] max-w-[390px] flex-col rounded-3xl border border-border/60 bg-background/95 p-5 text-center shadow-sm',
        isCurrent &&
          'border-primary/60 shadow-[0_18px_45px_rgba(20,184,166,0.14)] ring-1 ring-primary/20',
        className,
      )}
    >
      <header className="flex flex-col items-center">
        {isCurrent ? (
          <span className="mb-2 inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
            {t('portal.plans.badges.current')}
          </span>
        ) : (
          <span className="mb-2 h-7" />
        )}

        <div
          className={cn(
            'mb-3 flex h-14 w-14 items-center justify-center rounded-full',
            isCurrent ? 'bg-primary/10 text-primary' : 'bg-muted text-primary',
          )}
        >
          <Icon className="h-7 w-7" aria-hidden />
        </div>

        <h3 className="text-xl font-bold text-foreground">{displayName}</h3>

        {description ? (
          <p className="mx-auto mt-2 min-h-[48px] max-w-[280px] text-sm leading-6 text-muted-foreground">
            {description}
          </p>
        ) : (
          <div className="mt-2 min-h-[48px]" />
        )}
      </header>

      <div
        className={cn(
          'mt-4 rounded-2xl px-4 py-4 shadow-inner',
          isCurrent ? 'bg-primary/5' : 'bg-muted/35',
        )}
      >
        <p className="text-4xl font-extrabold leading-tight tracking-tight text-foreground">
          {priceLabel}
        </p>
        {originalPriceLabel ? (
          <div className="mt-1 flex items-center justify-center gap-2">
            <span className="text-sm text-muted-foreground line-through">{originalPriceLabel}</span>
            {displayPrice?.discountPercent ? (
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-bold text-primary">
                -{displayPrice.discountPercent}%
              </span>
            ) : null}
          </div>
        ) : null}

        <p className="mt-1 text-xs text-muted-foreground">
          {t(`portal.plans.perPeriod.${plan.billing_cycle}`, {
            defaultValue: t(`plans.periods.${plan.billing_cycle}`, {
              defaultValue: plan.billing_cycle,
            }),
          })}
        </p>

        {plan.trial_days !== null && plan.trial_days > 0 ? (
          <p className="mt-2 text-xs font-medium text-primary">
            {t('portal.plans.trialDays', { count: plan.trial_days })}
          </p>
        ) : null}
      </div>

      {featureLines.length > 0 ? (
        <ul className="mt-4 grid flex-1 grid-cols-1 gap-1.5 text-sm text-muted-foreground">
          {featureLines.map((feature) => (
            <li
              key={feature}
              className="flex items-center justify-center gap-2 rounded-xl bg-muted/25 px-3 py-1.5"
            >
              <Icons.CheckCircle className="h-4 w-4 shrink-0 text-primary" aria-hidden />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="flex-1" />
      )}

      <div className="mt-auto space-y-2 pt-5">
        <Button
          type="button"
          variant={isCurrent ? 'secondary' : 'default'}
          className="h-12 w-full rounded-xl text-sm font-bold"
          disabled={!isUpgradeEnabled}
          aria-disabled={!isUpgradeEnabled}
          onClick={
            isUpgradeEnabled
              ? () => {
                  onUpgradeClick?.(plan);
                }
              : undefined
          }
        >
          {ctaLabel}
        </Button>

        {!isCurrent ? (
          <p className="text-xs text-muted-foreground">🔒 عملية دفع آمنة ومشفرة</p>
        ) : null}

        {!isUpgradeEnabled && disabledReason ? (
          <p className="text-center text-xs text-muted-foreground">{disabledReason}</p>
        ) : null}

        {isFreeTrialPlan(plan) ? (
          <p className="text-xs font-medium text-primary">{t('portal.plans.badges.freeTrial')}</p>
        ) : null}
      </div>
    </article>
  );
}
